/**
 * identity-verification-view (public wizard) — pure status → UI view-model +
 * clean error copy (PR-DIDIT-6). No DOM / React / BE; the `./api` import is
 * type-only → elided by tsx, so this module (and its test) run env-free.
 *
 * Maps the normalized KYC status to a candidate-friendly view. We never name
 * "Didit" and never surface provider / secret / config detail.
 */
import type { KycActionErrorCode, KycNormalizedStatus } from './api';

export type IdentityVerificationTone =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

export type IdentityVerificationCtaKind = 'start' | 'resume' | 'retry';

export interface IdentityVerificationView {
  tone: IdentityVerificationTone;
  badgeLabel: string;
  description: string;
  /** Primary action, or null when the state is terminal/awaiting-operator. */
  cta: { kind: IdentityVerificationCtaKind; label: string } | null;
  /** Show the lightweight "Aggiorna stato" refresh while a session is in flight. */
  showRefresh: boolean;
}

/** Derive the card view-model from the normalized KYC status. */
export function deriveIdentityVerificationView(
  status: KycNormalizedStatus,
): IdentityVerificationView {
  switch (status) {
    case 'NOT_STARTED':
      return {
        tone: 'info',
        badgeLabel: 'Da completare',
        description:
          'Completa la verifica identità per permetterci di validare la richiesta.',
        cta: { kind: 'start', label: 'Avvia verifica identità' },
        showRefresh: false,
      };
    case 'SESSION_CREATED':
    case 'IN_PROGRESS':
      return {
        tone: 'info',
        badgeLabel: 'Verifica identità in corso',
        description:
          'Abbiamo avviato la verifica. Completa i passaggi richiesti, poi aggiorna lo stato.',
        cta: { kind: 'resume', label: 'Riprendi verifica' },
        showRefresh: true,
      };
    case 'VERIFIED':
      return {
        tone: 'success',
        badgeLabel: 'Identità verificata',
        description:
          'La tua identità è stata verificata. Non devi fare altro per questo requisito.',
        cta: null,
        showRefresh: false,
      };
    case 'REVIEW_REQUIRED':
      return {
        tone: 'warning',
        badgeLabel: 'Verifica da controllare da operatore',
        description:
          'Puoi proseguire con gli altri requisiti. Un operatore controllerà l’esito.',
        cta: null,
        showRefresh: false,
      };
    case 'FAILED':
    case 'EXPIRED':
    case 'CANCELLED':
      return {
        tone: 'danger',
        badgeLabel: 'Verifica non completata',
        description:
          'La verifica identità non è stata completata. Puoi ripeterla quando vuoi.',
        cta: { kind: 'retry', label: 'Ripeti verifica' },
        showRefresh: false,
      };
    case 'PROVIDER_ERROR':
      return {
        tone: 'danger',
        badgeLabel: 'Verifica non disponibile',
        description:
          'Verifica identità temporaneamente non disponibile. Riprova più tardi.',
        cta: { kind: 'retry', label: 'Riprova' },
        showRefresh: false,
      };
  }
}

/**
 * Whether the dedicated "Verifica identità" step should offer "Continua ai
 * documenti", and how prominently (PR-DIDIT-6B):
 *   - VERIFIED / REVIEW_REQUIRED → 'primary' (verification done or operator-
 *     reviewed; the natural next action is to continue)
 *   - PROVIDER_ERROR → 'secondary' (honest-mode / provider down: don't block the
 *     rest of the flow, but keep it a soft escape)
 *   - everything else → 'none' (the candidate must act on the verification first)
 *
 * The honest-mode escape after a failed "Avvia" click (status still NOT_STARTED
 * but action error PROVIDER_UNAVAILABLE) is handled at the component level.
 */
export type ContinueAffordance = 'none' | 'primary' | 'secondary';

export function continueAffordanceForStatus(
  status: KycNormalizedStatus,
): ContinueAffordance {
  switch (status) {
    case 'VERIFIED':
    case 'REVIEW_REQUIRED':
      return 'primary';
    case 'PROVIDER_ERROR':
      return 'secondary';
    default:
      return 'none';
  }
}

/** Compact one-word status for the read-only summary on the documents page. */
export function compactIdentityLabel(status: KycNormalizedStatus): string {
  switch (status) {
    case 'VERIFIED':
      return 'verificata';
    case 'REVIEW_REQUIRED':
      return 'da controllare';
    case 'SESSION_CREATED':
    case 'IN_PROGRESS':
      return 'in corso';
    case 'FAILED':
    case 'EXPIRED':
    case 'CANCELLED':
      return 'non completata';
    case 'PROVIDER_ERROR':
      return 'non disponibile';
    case 'NOT_STARTED':
    default:
      return 'da completare';
  }
}

/** Clean, non-technical copy for an action error. Never leaks provider detail. */
export function messageForKycError(code: KycActionErrorCode): string {
  switch (code) {
    case 'UPLOAD_TOKEN_INVALID':
      // Handled at the call site (onSessionExpired); defensive fallback copy.
      return 'Sessione scaduta. Invia nuovamente la richiesta.';
    case 'PROVIDER_UNAVAILABLE':
      return 'Verifica identità temporaneamente non disponibile. Riprova più tardi.';
    case 'RATE_LIMITED':
      return 'Troppe richieste, attendi qualche istante e riprova.';
    case 'NETWORK_ERROR':
      return 'Problema di connessione. Riprova.';
    case 'UNKNOWN':
    default:
      return 'Si è verificato un errore. Riprova.';
  }
}
